import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { PACSConnectionSetting } from '@features/analysis/analysisModal/SettingsModal/SettingsModalSlice';
import { SETTING_CONTENTS_NAMES } from 'config/settingsConfig';
// import './PACSConnectionSetting.scss';
import styles from  './PACSConnectionSetting.module.scss';
import Switch from '@components/Switch';
import Input from '@components/Input';
import { SETTING_PACS_CONNECTION_COLUMN } from 'config/tableColumns';
import Button from '@components/Button';
import { useSettingsModal } from '@features/analysis/analysisModal/SettingsModal/useSettingsModal';
import Modal from '@components/Modal';
import { useModal } from '@components/Modal/ModalProvider';
import Dropdown from '@components/Dropdown';
import { ReactComponent as Close } from 'img/btn-close.svg';
import PACSTable from '@components/Table/PACSTable';
import { DicomNodeParams, initDicomNodeParams } from '@models/pacs';
import useGetDicomNodes, {
  useDeleteDicomNode,
  useGetEcho,
  useGetEchos,
  usePatchDicomNode,
  usePostDicomNode,
} from '@hooks/PACS/usePacs';
import { configStore } from 'config/configStore';
import useGetDicomNodeConfig, {
  usePatchDicomNodeConfig,
} from '@hooks/DicomNode/useDicomNode';
import { useAuthPage } from '@features/auth/useAuthPage';
import { DicomNodeConfigResponse } from '@models/DicomNode';
interface PACSConnectionSettingProps {}

export interface ServerModalProps {
  onClose: () => void;
  data?: DicomNodeParams | typeof initDicomNodeParams;
  mode: 'add' | 'modify';
  onChangeRow?: (row: DicomNodeParams) => void;
}

function ServerModal({
  onClose,
  data = initDicomNodeParams,
  mode,
  onChangeRow,
}: ServerModalProps) {
  const [Name, setName] = useState(data.description);
  const [AETitle, setAETitle] = useState(data.aetitle);
  const [Address, setAddress] = useState(data.ip);
  const [Port, setPort] = useState(data.port);
  const { handleDefaultServer, pacsConnection } = useSettingsModal();
  const [isChecked, setIsChecked] = useState(
    pacsConnection.defaultServer.some((server) => server.id === data.id),
  );

  type ProtocolKey =
    keyof typeof SETTING_CONTENTS_NAMES.pacsConnection.protocols;
  const [Protocol, setProtocol] = useState<ProtocolKey>('CMove');

  const options = useMemo(
    () =>
      Object.entries(SETTING_CONTENTS_NAMES.pacsConnection.protocols).map(
        ([key, value]) => ({ value: key, label: value }),
      ),
    [],
  );

  const postMutation = usePostDicomNode();
  const patchMutation = usePatchDicomNode();

  useEffect(() => {
    setIsChecked(
      configStore.get().defaultServer.some((server) => server.id === data.id),
    );
  }, [pacsConnection, data.id]);

  const handleSubmit = () => {
    const basePayload = {
      description: Name,
      aetitle: AETitle,
      ip: Address,
      port: Port,
      defaultServer: isChecked,
      defaultQuery: false,
      defaultReportUpload: true,
      supportJpeg: false,
    };

    const withIdPayload: DicomNodeParams = { id: data.id, ...basePayload };

    const updateDefaultServer = () => {
      if (isChecked) {
        handleDefaultServer([
          ...configStore
            .get()
            .defaultServer.filter((s) => s.id !== withIdPayload.id),
          withIdPayload,
        ]);
      } else {
        handleDefaultServer(
          configStore
            .get()
            .defaultServer.filter((s) => s.id !== withIdPayload.id),
        );
      }
    };

    const commonOptions = {
      onSuccess: () => {
        updateDefaultServer();
        onChangeRow?.(withIdPayload);
        onClose();
      },
    };

    if (mode === 'add') {
      postMutation.mutate(basePayload, commonOptions);
    } else {
      patchMutation.mutate(
        { node: basePayload, nodeId: data.id },
        commonOptions,
      );
    }
  };

  return (
    <Modal
      type="page"
      close={onClose}
      description={
        <div className={styles.modalContainer}>
          {/* 상단 */}
          <div className={styles.modalHeader}>
            <span className={styles.modalHeaderLabel}>{SETTING_CONTENTS_NAMES.pacsConnection.title}</span>
            <Close onClick={onClose} />
          </div>

          {/* 입력폼 */}
          <div className={styles.modalBlock}>
            <div className={styles.modalLabel}>
              <div className={styles.modalNumberInput}>
                <span className={styles.modalNILabel}>{SETTING_CONTENTS_NAMES.pacsConnection.name}</span>
                <Input
                  className='setting-pacs-modal'
                  placeholder="Input"
                  value={Name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className={styles.modalNumberInput}>
                <span className={styles.modalNILabel}>{SETTING_CONTENTS_NAMES.pacsConnection.AETitle}</span>
                <Input
                  className='setting-pacs-modal'
                  placeholder="Input"
                  value={AETitle}
                  onChange={(e) => setAETitle(e.target.value)}
                />
              </div>
            </div>
            <div className={styles.modalLabel}>
              <div className={styles.modalNumberInput}>
                <span className={styles.modalNILabel}>{SETTING_CONTENTS_NAMES.pacsConnection.address}</span>
                <Input
                  className='setting-pacs-modal'
                  placeholder="Input"
                  value={Address}
                  onChange={(e) => setAddress(e.target.value)}
                />
              </div>
              <div className={styles.modalNumberInput}>
                <span className={styles.modalNILabel}>{SETTING_CONTENTS_NAMES.pacsConnection.port}</span>
                <Input
                className='setting-pacs-modal'
                  placeholder="Input"
                  value={Port}
                  onChange={(e) => setPort(Number(e.target.value))}
                />
              </div>
            </div>
            <div className={styles.modalLabelInput}>
              <div className={styles.modalLabelInput2}>
                <div className={styles.modalLabelInput3}>
                  <span className={styles.modalNILabel}>{SETTING_CONTENTS_NAMES.pacsConnection.protocol}</span>
                  <Dropdown
                    className='protocol-dropdown'
                    placeholder="Select Protocol"
                    options={options}
                    value={Protocol}
                    onChange={(value) => setProtocol(value as ProtocolKey)}
                  />
                </div>
              </div>
            {mode !== 'add' && (
              <div className={styles.modalNumberInput}>
                <span className={styles.modalNILabel}>{SETTING_CONTENTS_NAMES.pacsConnection.setasdefault}</span>
                <Switch checked={isChecked} onChange={setIsChecked} />
              </div>
            )}
            </div>
          </div>

          {/* 버튼 */}
          <div className={styles.modalAction}>
            {/* <div className="pacs-server-buttons-container"> */}
              <Button
              className='action-control cancel'
              onClick={onClose}>
                {SETTING_CONTENTS_NAMES.pacsConnection.cancel}
              </Button>
              <Button
              className='action-control confirm'
              color="cardio" onClick={handleSubmit}>
                {SETTING_CONTENTS_NAMES.pacsConnection.confirm}
              </Button>
            {/* </div> */}
          </div>
        </div>
      }
    />
  );
}

function PACSConnectionSetting({
  children,
}: PACSConnectionSettingProps & {
  children?: React.ReactNode;
}): React.ReactElement {
  const { openModal, closeModal } = useModal();
  const { user } = useAuthPage();
  const { pacsConnection } = useSettingsModal();

  const { data: nodes } = useGetDicomNodes();
  const { data: echos } = useGetEchos(nodes);
  const { data: dicomNodeConfig } = useGetDicomNodeConfig();
  const deleteMutation = useDeleteDicomNode();
  const echoMutation = useGetEcho();
  const patchMutation = usePatchDicomNodeConfig();

  const [selectedKey, setSelectedKey] = useState<string | undefined>(undefined);
  const [selectedRow, setSelectedRow] = useState<DicomNodeParams | undefined>();
  const [aetitle, setAetitle] = useState(dicomNodeConfig?.dicomAetitle ?? '');
  const [port, setPort] = useState(dicomNodeConfig?.dicomPort ?? '');
  const [seriesNumberBase, setSeriesNumberBase] = useState(dicomNodeConfig?.seriesNumberBase ?? 0);
  const latestValues = useRef<{
    aetitle: string;
    port: string;
    seriesNumberBase:Number;
    dicomNodeConfig?: DicomNodeConfigResponse;
  }>({
    aetitle: '',
    port: '',
    seriesNumberBase:0,
    dicomNodeConfig: undefined,
  });
  const isAdmin = user?.role.name == 'admin';

  const handleOpenPacsServerModal = useCallback(
    (mode: 'add' | 'modify') => {
      let modalId = '';
      modalId = openModal(
        <ServerModal
          mode={mode}
          data={mode === 'modify' ? selectedRow : undefined}
          onClose={() => closeModal(modalId)}
          onChangeRow={(row) => {
            setSelectedRow(row);
            setSelectedKey(row.id);
          }}
        />,
      );
    },
    [openModal, closeModal, selectedRow],
  );

  const tableData = useMemo(() => {
    const base = echos ?? nodes ?? [];
    return base.map((node) => ({
      ...node,
      defaultServer: configStore
        .get()
        .defaultServer.some((s) => s.id === node.id),
    }));
  }, [echos, nodes, pacsConnection]);

  useEffect(() => {
    if (dicomNodeConfig) {
      setAetitle(dicomNodeConfig.dicomAetitle ?? '');
      setPort(dicomNodeConfig.dicomPort ?? '');
      setSeriesNumberBase(dicomNodeConfig.seriesNumberBase ?? '');
    }
  }, [dicomNodeConfig]);

  useEffect(() => {
    latestValues.current = { aetitle, port, seriesNumberBase,  dicomNodeConfig };
  }, [aetitle, port, seriesNumberBase, dicomNodeConfig]);

  useEffect(() => {
    return () => {
      const { aetitle, port, dicomNodeConfig, seriesNumberBase } = latestValues.current;

      if (isAdmin && dicomNodeConfig) {
        if (
          aetitle !== dicomNodeConfig.dicomAetitle ||
          port !== dicomNodeConfig.dicomPort ||
          seriesNumberBase != dicomNodeConfig.seriesNumberBase
        ) {
          console.log("seriesNumberBase: ", seriesNumberBase)
          patchMutation.mutate({
            aetitle,
            port,
            seriesNumberBase: Number(seriesNumberBase),
          });
        }
      }
    };
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.frame}>
        <span className={styles.frameHeader}>
          {SETTING_CONTENTS_NAMES.pacsConnection.client}
        </span>
        <div className={styles.frameDivider} />
        <div className={styles.block}>
          <div className={styles.blockBlock}>
            <div className={styles.blockBlockBlock}>
              <div className={styles.numberInput}>
                <span className={styles.niLabel}>
                  {SETTING_CONTENTS_NAMES.pacsConnection.AETitle}{' '}
                </span>
                <Input
                  placeholder="Input"
                  value={aetitle}
                  className="setting-pacs-input"
                  onChange={(e) => setAetitle(e.target.value)}
                  disabled={!isAdmin}
                />
              </div>
              <div className={styles.numberInput}>
                <span className={styles.niLabel}>
                  {SETTING_CONTENTS_NAMES.pacsConnection.port}{' '}
                </span>
                <Input
                  placeholder="Number Input"
                  value={port}
                  className="setting-pacs-input"
                  onChange={(e) => setPort(e.target.value)}
                  disabled={!isAdmin}
                />
              </div>
              <div className={styles.numberInput}>
                <span className={styles.niLabel}>
                  {SETTING_CONTENTS_NAMES.pacsConnection.seriesNumberBase}{' '}
                </span>
                <Input
                  placeholder="Input"
                  value={seriesNumberBase.toString()}
                  className="setting-pacs-input"
                  onChange={(e) => setSeriesNumberBase(Number(e.target.value))}
                  disabled={!isAdmin}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.frame}>
        <div className={styles.sarverHeader}>
          <span className={styles.header}>
            {' '}
            {SETTING_CONTENTS_NAMES.pacsConnection.server}{' '}
          </span>
          <div className={styles.divider} />
        </div>

        <div className={styles.block}>
          <div className={styles.serverNumberInput}>
            <span className={styles.serverLabel}>
              {' '}
              {SETTING_CONTENTS_NAMES.pacsConnection.serverList}{' '}
            </span>
            <div className={styles.serverContent}>
              <PACSTable
                columns={SETTING_PACS_CONNECTION_COLUMN}
                data={tableData}
                getRowId={(r) => r.id}
                onRowClick={(key, row) => {
                  setSelectedKey(key.toString());
                  setSelectedRow(row as DicomNodeParams);
                }}
                selectedKey={selectedKey}
              />
            </div>
          </div>
          <div className={styles.serverButtons}>
            {isAdmin && (
              <div className={styles.adminButtons}>
                <div className={styles.adminNumberInput1}>
                  <Button
                    className="action-control add"
                    onClick={() => handleOpenPacsServerModal('add')}
                    color="cardio"
                  >
                    Add
                  </Button>
                  <Button
                    className="action-control delete"
                    onClick={() => deleteMutation.mutate(selectedKey!)}
                    disabled={!selectedKey}
                    color="cardio"
                  >
                    Delete
                  </Button>
                </div>
                <div className={styles.adminNumberInput2}>
                  <Button
                    className="action-control modify"
                    onClick={() => handleOpenPacsServerModal('modify')}
                    disabled={!selectedKey}
                    color="cardio"
                  >
                    Modify
                  </Button>
                </div>
              </div>
            )}
            <div className={styles.adminButtons2}>
              <Button
                className="action-control echo"
                onClick={() => echoMutation.mutate(selectedKey!)}
                disabled={!selectedKey}
                color="cardio"
              >
                Echo
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PACSConnectionSetting;
